import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Settings2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

import { useApp } from '@/presentation/hooks/use-app.js';

export interface DataTableColumn<T> {
    key: string;
    header: string;
    render?: (row: T) => ReactNode;
    sortable?: boolean;
    sortValue?: (row: T) => string | number | boolean | null | undefined;
    hideable?: boolean;
    width?: string | number;
    align?: 'left' | 'center' | 'right';
}

interface Props<T> {
    columns: DataTableColumn<T>[];
    rows: T[];
    getRowKey: (row: T) => string;
    onRowClick?: (row: T) => void;
    page?: number;
    rowsPerPage?: number;
    total?: number;
    onPageChange?: (page: number) => void;
    onRowsPerPageChange?: (limit: number) => void;
    rowsPerPageOptions?: number[];
    loading?: boolean;
    emptyMessage?: string;
    showColumnToggle?: boolean;
}

type Order = 'asc' | 'desc';

const HEADER_CELL_SX = {
    fontSize: 11,
    fontWeight: 600,
    color: 'text.secondary',
    textTransform: 'uppercase',
    bgcolor: 'action.hover',
    borderBottom: 1,
    borderColor: 'divider',
    whiteSpace: 'nowrap'
} as const;

function isColumnSortable<T>(column: DataTableColumn<T>): boolean {
    return column.sortable !== false;
}

function getSortValue<T>(row: T, column: DataTableColumn<T>): string | number | boolean | null | undefined {
    if (column.sortValue) {
        return column.sortValue(row);
    }
    return (row as Record<string, unknown>)[column.key] as string | number | boolean | null | undefined;
}

function compareSortValues(a: string | number | boolean | null | undefined, b: string | number | boolean | null | undefined, order: Order): number {
    if (a == null && b == null) {
        return 0;
    }
    if (a == null) {
        return 1;
    }
    if (b == null) {
        return -1;
    }
    if (typeof a === 'number' && typeof b === 'number') {
        return order === 'asc' ? a - b : b - a;
    }
    if (typeof a === 'boolean' && typeof b === 'boolean') {
        const av = a ? 1 : 0;
        const bv = b ? 1 : 0;
        return order === 'asc' ? av - bv : bv - av;
    }
    const cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
    return order === 'asc' ? cmp : -cmp;
}

export function DataTable<T>({
    columns,
    rows,
    getRowKey,
    onRowClick,
    page = 0,
    rowsPerPage = 10,
    total,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions = [10, 25, 50],
    loading,
    emptyMessage,
    showColumnToggle = true
}: Props<T>) {
    const { translate } = useApp();
    const [orderBy, setOrderBy] = useState<string | null>(null);
    const [order, setOrder] = useState<Order>('asc');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());

    const handleSort = (key: string) => {
        if (orderBy === key) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(key);
            setOrder('asc');
        }
    };

    const visibleColumns = columns.filter((c) => !hiddenColumns.has(c.key));

    const sortedRows = useMemo(() => {
        if (!orderBy) {
            return rows;
        }
        const column = columns.find((c) => c.key === orderBy);
        if (!column || !isColumnSortable(column)) {
            return rows;
        }
        return [...rows].sort((left, right) => compareSortValues(getSortValue(left, column), getSortValue(right, column), order));
    }, [rows, orderBy, order, columns]);

    const toggleColumn = (key: string) => {
        setHiddenColumns((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const hideableColumns = columns.filter((c) => c.hideable !== false);

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: 'background.paper' }}>
            {showColumnToggle && hideableColumns.length > 0 && (
                <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
                >
                    <Tooltip title={translate('table.columns')}>
                        <IconButton
                            size="small"
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            sx={{
                                color: 'text.secondary',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'action.hover', color: 'text.primary', borderColor: 'text.secondary' }
                            }}
                        >
                            <Settings2 size={15} />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={!!anchorEl}
                        onClose={() => setAnchorEl(null)}
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: 1,
                                    minWidth: 200,
                                    borderRadius: 1.5,
                                    boxShadow: 3,
                                    py: 0.5
                                }
                            }
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ px: 2, py: 1, display: 'block', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
                        >
                            {translate('table.columns')}
                        </Typography>
                        {hideableColumns.map((col) => {
                            const hidden = hiddenColumns.has(col.key);
                            return (
                                <MenuItem
                                    key={col.key}
                                    onClick={() => toggleColumn(col.key)}
                                    sx={{
                                        py: 1,
                                        px: 2,
                                        gap: 1.5,
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            border: 1.5,
                                            borderColor: hidden ? 'divider' : 'primary.main',
                                            bgcolor: hidden ? 'transparent' : 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        {!hidden && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'white' }} />}
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: hidden ? 400 : 500, color: hidden ? 'text.disabled' : 'text.primary' }}>
                                        {col.header || col.key}
                                    </Typography>
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </Box>
            )}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {visibleColumns.map((col) => (
                                <TableCell key={col.key} align={col.align} sx={{ ...HEADER_CELL_SX, width: col.width }}>
                                    {isColumnSortable(col) ? (
                                        <TableSortLabel active={orderBy === col.key} direction={orderBy === col.key ? order : 'asc'} onClick={() => handleSort(col.key)}>
                                            {col.header}
                                        </TableSortLabel>
                                    ) : (
                                        col.header
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('common.loading')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : sortedRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {emptyMessage ?? translate('common.noData')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedRows.map((row) => (
                                <TableRow
                                    key={getRowKey(row)}
                                    hover={!!onRowClick}
                                    onClick={() => onRowClick?.(row)}
                                    sx={{ cursor: onRowClick ? 'pointer' : 'default', '&:last-child td': { borderBottom: 0 } }}
                                >
                                    {visibleColumns.map((col) => (
                                        <TableCell key={col.key} align={col.align}>
                                            {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '')}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {onPageChange && (
                <TablePagination
                    component="div"
                    count={total ?? rows.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(_, p) => onPageChange(p)}
                    onRowsPerPageChange={(e) => onRowsPerPageChange?.(Number(e.target.value))}
                    rowsPerPageOptions={rowsPerPageOptions}
                    labelRowsPerPage={translate('table.rowsPerPage')}
                    sx={{ borderTop: 1, borderColor: 'divider' }}
                />
            )}
        </Paper>
    );
}
